import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * LegacyRedirects Component
 * 
 * Handles redirects from old WordPress URLs to new SPA routes.
 * 
 * IMPORTANT SEO NOTE:
 * This is a client-side redirect (not a true HTTP 301) because:
 * - React SPAs run in the browser after the initial HTML is served
 * - The server always returns 200 OK with the SPA shell
 * - The redirect happens via JavaScript after the page loads
 * 
 * For true 301 redirects, you would need:
 * - Server-side configuration (nginx, Apache)
 * - Edge/CDN rules (Cloudflare Page Rules, Vercel redirects)
 * - Emergent platform-level redirect configuration
 * 
 * This client-side approach:
 * - Works for users (they land on correct page)
 * - Google will eventually update its index (though slower than 301)
 * - Prevents "Service Not Found" errors for legacy URLs
 */

// Legacy URL mapping: old path -> new path
const LEGACY_REDIRECTS = {
  // Critical service redirects - redirect to dedicated SEO-optimized pages
  '/services/sap-calculations': '/sap-calculations',
  '/services/u-value-calculations': '/u-value-calculations',
  '/services/u_values': '/u-value-calculations',
  '/services/floor-plans': '/services/marketing_plans',
  '/services/lease-plans': '/lease-plans',
  
  // EPC service redirects
  '/services/epc': '/services/domestic_epc',
  '/services/epc-services/bristol': '/services/domestic_epc',
  '/services/epc-services/plymouth': '/services/domestic_epc',
  '/services/epc-energy-performance-certificate': '/services/domestic_epc',
  '/services/epc-energy-performance-certificate/exeter': '/services/domestic_epc',
  
  // Other service redirects
  '/non-domestic-epc': '/services/commercial_warehouse',
  '/retrofit-coordinator': '/services/retrofit',
  '/epc-exeter': '/services/domestic_advice',
  '/important-changes-to-commercial-epc-results': '/services/commercial_advice',
  
  // Legacy page redirects to home
  '/epc-plymouth-guide': '/',
  '/category/epc': '/',
  '/category/epc/': '/',
  
  // Additional common WordPress patterns
  '/services/epc/': '/services/domestic_epc',
  '/services/sap-calculations/': '/sap-calculations',
  '/services/u-value-calculations/': '/u-value-calculations',
  '/services/u_values/': '/u-value-calculations',
  '/services/floor-plans/': '/services/marketing_plans',
  '/services/lease-plans/': '/lease-plans',
};

/**
 * Normalize path by removing trailing slash (except for root)
 */
const normalizePath = (path) => {
  if (path === '/') return path;
  return path.endsWith('/') ? path.slice(0, -1) : path;
};

/**
 * Find redirect target for a given path
 * Checks both with and without trailing slash
 */
const findRedirectTarget = (pathname) => {
  const normalized = normalizePath(pathname);
  const withSlash = normalized + '/';
  
  // Check exact match first
  if (LEGACY_REDIRECTS[pathname]) {
    return LEGACY_REDIRECTS[pathname];
  }
  
  // Check normalized (without trailing slash)
  if (LEGACY_REDIRECTS[normalized]) {
    return LEGACY_REDIRECTS[normalized];
  }
  
  // Check with trailing slash
  if (LEGACY_REDIRECTS[withSlash]) {
    return LEGACY_REDIRECTS[withSlash];
  }
  
  return null;
};

/**
 * LegacyRedirect component that handles the actual redirect
 */
const LegacyRedirect = () => {
  const location = useLocation();
  const redirectTarget = findRedirectTarget(location.pathname);
  
  useEffect(() => {
    if (redirectTarget) {
      // Log redirect for debugging/analytics
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Legacy redirect: ${location.pathname} → ${redirectTarget}`);
      }
    }
  }, [location.pathname, redirectTarget]);
  
  if (redirectTarget) {
    // Use replace to prevent back button issues
    return <Navigate to={redirectTarget} replace />;
  }
  
  // No redirect needed, return null (this shouldn't happen if routes are set up correctly)
  return null;
};

/**
 * Hook to check if current path needs redirect
 * Can be used in other components if needed
 */
export const useLegacyRedirect = () => {
  const location = useLocation();
  return findRedirectTarget(location.pathname);
};

/**
 * Get all legacy paths for route registration
 */
export const getLegacyPaths = () => {
  return Object.keys(LEGACY_REDIRECTS);
};

export default LegacyRedirect;
